import { FC, useEffect, useRef, useState } from 'react';

import { Button, Spin } from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';

import styles from './ConfirmEmail.module.scss';
import { getFingerPrint, saveAccessToken } from '../../../helpers/utils/auth';
import { authApi } from '../../../shared/common/api/endpoints/authApi';
import { routeNames } from '../../../shared/common/constants/routes';
import { useActions } from '../../../shared/common/hooks/useActions';

/**
 * Переход по ссылке «Войти в личный кабинет» из письма после регистрации.
 *
 * Регистрация на сайте больше не спрашивает пароль: человек оставляет имя, почту
 * и телефон, а сюда попадает из письма. Мы отдаём токен из ссылки на бэкенд, тот
 * отмечает подтверждение и возвращает обычную пару токенов — поэтому дальше
 * человек оказывается в кабинете вошедшим. Логин и пароль он уже получил тем же
 * письмом, из которого пришёл.
 *
 * Про подтверждение почты на экране не пишем: для человека это не шаг, который
 * надо пройти, — он нажал кнопку в письме и просто заходит. Отметка о
 * подтверждении ставится молча, на бэкенде.
 */
const ConfirmEmail: FC = () => {
  document.title = 'Вход в личный кабинет';

  const navigate = useNavigate();
  const [ searchParams ] = useSearchParams();
  const { setIsAuth } = useActions();

  const [ error, setError ] = useState('');
  const started = useRef(false);   // React 18 в dev монтирует дважды — второй запрос не нужен

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const token = searchParams.get('token');

    if (!token) {
      setError('Ссылка неполная. Откройте письмо ещё раз и нажмите кнопку целиком.');
      return;
    }

    (async () => {
      try {
        const fingerprint = await getFingerPrint();
        const response = await authApi.confirmEmail(token, fingerprint);

        if (response.status === 200 && response.data?.access_token) {
          saveAccessToken(response.data.access_token);
          setIsAuth(true);
          navigate(routeNames.home);
          return;
        }

        setError('Не удалось войти по ссылке. Попробуйте открыть её ещё раз.');

      } catch (err: any) {
        const fromServer = err?.response?.data?.token;

        setError(
          typeof fromServer === 'string'
            ? fromServer
            : 'Не удалось войти по ссылке. Попробуйте позже или войдите по логину и паролю из письма.'
        );
      }
    })();
  }, []);

  return (
    <div className={styles.confirmPage}>
      <div className={styles.confirmModal}>
        <div className={styles.logoWrapper}>
          <img alt="SCAN" src="/logo.jpg" />
        </div>

        {error === '' ? (
          <>
            <div className={styles.title}>Открываем кабинет…</div>
            <p className={styles.text}>Секунду — вы уже почти внутри.</p>
            <Spin size="large" />
          </>
        ) : (
          <>
            <div className={styles.title}>Ссылка не сработала</div>
            <p className={styles.text}>{error}</p>
            <Button type="primary" size="large" onClick={() => navigate(routeNames.login)}>
              Перейти ко входу
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default ConfirmEmail;
